import React, { useContext, useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Box,
  Grid,
  CardMedia,
  CardActionArea,
  Chip,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import {
  Star,
  Image as ImageIcon,
  KeyboardArrowDown,
  Close,
  Delete as DeleteIcon // 追加
} from '@mui/icons-material';
import ApiFetch from '../ApiFetch';
import { LoginInfo } from '../../App';
import Thumb from '../common/Thumb';
import { useNavigate } from 'react-router';
import Header from '../Header';
import Category from '../Category';
import Footer from '../Footer';

// Define constants for model types and style types
const MODEL_TYPES = [
  { key: 'all', name: 'すべて' },
  { key: 'SD1.5', name: 'SD1.5' },
  { key: 'SDXL', name: 'SDXL' },
  { key: 'SD3', name: 'SD3' },
  { key: 'DALL-E3', name: 'OpenAI' }
];

const STYLE_TYPES = [
  { key: 'all', name: 'すべて' },
  { key: 'illustration', name: 'イラスト' },
  { key: 'semirealism', name: 'セミリアル' },
  { key: 'photorealistic', name: 'フォトリアル' },
  { key: 'common', name: '汎用' }
];

/**

FilterHeader Component - For filtering models
*/
const FilterHeader = ({
  showFavorites,
  setShowFavorites,
  selectedStyle,
  setSelectedStyle,
  selectedModelType,
  setSelectedModelType,
  searchQuery,
  setSearchQuery
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const filterContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Button
        fullWidth
        variant={showFavorites ? "contained" : "outlined"}
        onClick={() => setShowFavorites(!showFavorites)}
        startIcon={<Star />}
      >
        お気に入り
      </Button>
      <FormControl fullWidth>
        <InputLabel>画風</InputLabel>
        <Select
          value={selectedStyle}
          onChange={(e) => setSelectedStyle(e.target.value)}
          label="画風"
        >
          {STYLE_TYPES.map(style => (
            <MenuItem key={style.key} value={style.key}>
              {style.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>モデル種別</InputLabel>
        <Select
          value={selectedModelType}
          onChange={(e) => setSelectedModelType(e.target.value)}
          label="モデル種別"
        >
          {MODEL_TYPES.map(type => (
            <MenuItem key={type.key} value={type.key}>
              {type.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        placeholder="検索"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
    </Box>
  );

  return (
    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6">モデル一覧</Typography>
        {isMobile ? (
          // Mobile accordion layout
          <Accordion
            sx={{
              boxShadow: 'none',
              '&:before': { display: 'none' },
              bgcolor: 'transparent'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 0,
                '& .MuiAccordionSummary-content': {
                  margin: 0
                }
              }}
            >
              <Typography color="primary">フィルター</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              {filterContent}
            </AccordionDetails>
          </Accordion>
        ) : (
          // Desktop layout
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button
              variant={showFavorites ? "contained" : "outlined"}
              onClick={() => setShowFavorites(!showFavorites)}
              startIcon={<Star />}
            >
              お気に入り
            </Button>

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>画風</InputLabel>
              <Select
                value={selectedStyle}
                onChange={(e) => setSelectedStyle(e.target.value)}
                label="画風"
              >
                {STYLE_TYPES.map(style => (
                  <MenuItem key={style.key} value={style.key}>
                    {style.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>モデル種別</InputLabel>
              <Select
                value={selectedModelType}
                onChange={(e) => setSelectedModelType(e.target.value)}
                label="モデル種別"
              >
                {MODEL_TYPES.map(type => (
                  <MenuItem key={type.key} value={type.key}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              placeholder="検索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: { sm: 200 } }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**

ModelCard Component - For displaying a single model card
*/
export const ModelCard = ({
  model,
  isSelected,
  onSelect,
  onToggleFavorite,
  onShowGallery,
  onShowInfo,
  isLoading,
  onEditButton, // 追加
  onDeleteButton // 追加
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      '&::before': {
        content: '""',
        display: 'block',
        paddingTop: '133%'
      }
    }}>
      <Card
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          cursor: 'pointer',
          transition: 'all 0.3s ease-in-out',
          outline: isSelected ? '3px solid #f50057' : 'none',
          transform: isSelected ? 'scale(1.02)' : 'none',
          boxShadow: isSelected ? '0 0 20px rgba(245, 0, 87, 0.25)' : 'none',
          '&:hover': {
            transform: isSelected ? 'scale(1.02) translateY(-8px)' : 'translateY(-8px)',
            boxShadow: isSelected ? '0 8px 20px rgba(245, 0, 87, 0.3)' : '0 8px 16px rgba(0,0,0,0.2)',
            '& .MuiCardMedia-root': {
              transform: 'scale(1.05)',
            },
            '& .model-info-overlay': {
              opacity: 1,
              transform: 'translateY(0)',
            }
          },
          '&:hover::after': isSelected ? {
            content: '""',
            position: 'absolute',
            top: -3,
            left: -3,
            right: -3,
            bottom: -3,
            border: '3px solid #f50057',
            borderRadius: '4px',
            animation: 'pulse 2s infinite',
            pointerEvents: 'none'
          } : {},
          '@keyframes pulse': {
            '0%': {
              opacity: 1,
              transform: 'scale(1)',
            },
            '50%': {
              opacity: 0.5,
              transform: 'scale(1.02)',
            },
            '100%': {
              opacity: 1,
              transform: 'scale(1)',
            }
          }
        }}
        onClick={onSelect}
      >
        <CardActionArea sx={{ overflow: 'hidden', aspectRatio: '3 / 4', width: '100%' }}>
          {model.sample || model.img ? (
            <CardMedia
              component="img"
              sx={{
                width: '100%',
                height: '100%',
                aspectRatio: '3 / 4',
                transition: 'transform 0.3s ease-in-out',
                objectFit: 'cover',
                display: 'block',
              }}
              image={model.sample ? model.sample : "/img/" + model.img}
              alt={model.sample || model.upload_status === 1 ? model.model_name + " " + model.name : model.name}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                aspectRatio: '3 / 4',
                minHeight: 180,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.200',
                color: 'grey.500',
                flexDirection: 'column',
              }}
            >
              <ImageIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="caption">No Image</Typography>
            </Box>
          )}

          {/* Model type chip */}
          <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
            <Chip
              label={model.model_type === "SDXL" ? "XL" : model.model_type}
              color={model.model_type === 'SD1.5' ? 'default' : 'primary'}
              size="small"
              sx={{
                transition: 'transform 0.2s ease',
                backgroundColor: model.model_type === 'SD1.5' ? 'rgba(128, 128, 128, 0.95)' : undefined,
                color: model.model_type === 'SD1.5' ? 'white' : undefined,
                '&:hover': {
                  transform: 'scale(1.05)',
                }
              }}
            />
          </Box>

          {/* Model div chip */}
          <Box sx={{ position: 'absolute', top: 8, left: 60 }}>
            <Chip
              label={model.model_div}
              color={'primary'}
              size="small"
              sx={{
                transition: 'transform 0.2s ease',
                backgroundColor: model.model_type === 'SD1.5' ? 'rgba(128, 128, 128, 0.95)' : undefined,
                color: model.model_type === 'SD1.5' ? 'white' : undefined,
                '&:hover': {
                  transform: 'scale(1.05)',
                }
              }}
            />
          </Box>

          {/* Action buttons */}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              gap: isMobile ? 0 : 1,
              alignItems: 'center',
            }}
          >
            {/* Info button */}
            {onShowInfo && <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onShowInfo(e);
              }}
              sx={{
                bgcolor: 'info.main',
                color: 'white',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'info.dark',
                  transform: 'scale(1.1)',
                },
                padding: '6px',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem',
                }
              }}
            >
              <InfoIcon />
            </IconButton>}

            {/* Gallery button */}
            {onShowGallery && <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onShowGallery(e);
              }}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'scale(1.1)',
                },
                padding: '6px',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem',
                }
              }}
            >
              <ImageIcon />
            </IconButton>}

            {/* Edit button */}
            {onEditButton && <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onEditButton(e);
              }}
              sx={{
                bgcolor: 'secondary.main',
                color: 'white',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'secondary.dark',
                  transform: 'scale(1.1)',
                },
                padding: '6px',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem',
                }
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="currentColor"/></svg>
            </IconButton>}

            {/* Delete button */}
            {onDeleteButton && <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onDeleteButton(e);
              }}
              sx={{
                bgcolor: 'error.main',
                color: 'white',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'error.dark',
                  transform: 'scale(1.1)',
                },
                padding: '6px',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem',
                }
              }}
            >
              <DeleteIcon />
            </IconButton>}

            {/* Favorite button */}
            {onToggleFavorite && <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(e);
              }}
              disabled={isLoading}
              sx={{
                color: model.favorite === 1 ? 'warning.main' : 'action.disabled',
                bgcolor: 'background.paper',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: isLoading ? 'none' : 'scale(1.1)',
                  bgcolor: 'background.paper',
                },
                padding: '6px',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem',
                }
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Star />
              )}
            </IconButton>}
          </Box>

          {/* Info overlay */}
          <Box
            className="model-info-overlay"
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              background: 'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.7))',
              color: 'white',
              transition: 'all 0.3s ease-in-out',
              opacity: 0,
              transform: 'translateY(20px)',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {model.sample || model.upload_status === 1 ? model.model_name + " " + model.name : model.name}
            </Typography>
            <Typography variant="body2" sx={{
              opacity: 0.9,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}>
              {model.description}
            </Typography>
          </Box>
        </CardActionArea>
      </Card>
    </Box>
  );
};

/**

ModelInfoDialog Component - Shows detailed model information
*/
export const ModelInfoDialog = ({ open, onClose, model }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!model) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              モデル情報
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          {/* Model info */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box
              component="img"
              src={model.sample ? model.sample : "/img/" + model.img}
              alt={model.sample || model.upload_status === 1 ? model.model_name + " " + model.name : model.name}
              sx={{ width: 80, height: 80, borderRadius: 1, objectFit: 'cover' }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                {model.sample || model.upload_status === 1 ? model.model_name + " " + model.name : model.name}
              </Typography>
              <Chip
                label={model.model_type}
                color={model.model_type === 'SD1.5' ? 'default' : 'primary'}
                size="small"
                sx={{
                  backgroundColor: model.model_type === 'SD1.5' ? 'rgba(128, 128, 128, 0.95)' : undefined,
                  color: model.model_type === 'SD1.5' ? 'white' : undefined,
                }}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              説明
            </Typography>
            <Typography variant="body1">
              {model.description}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              画風
            </Typography>
            <Typography variant="body1">
              {STYLE_TYPES.find(style => style.key === model.style)?.name}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**

GalleryDialog Component - Shows sample images from the model
*/
const GalleryDialog = ({ open, onClose, model, samples, isLoading, loginInfo, onShowDialog }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!model) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {model.sample || model.upload_status === 1 ? model.model_name + " " + model.name : model.name} - サンプル画像
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Grid container spacing={2}>
          {isLoading ?
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 4 }}>
              <CircularProgress />
            </Box> :
            samples.map((data, i) => {
              if (data.mute === 1 || (i > 0 && samples?.[i - 1]?.user_id === data.user_id) ||
                (data.user_id !== loginInfo.userId && data.search_ban === 1)) {
                return <span data-no={i} className="lazy" key={i}></span>;
              }
              return (
                <Thumb
                  data={data}
                  i={i}
                  dialog={onShowDialog}
                  key={i}
                  list={samples.filter(d => d.mute === 0)}
                  listName={"samples"}
                />
              );
            })
          }
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

/**

MessageDialog Component - Generic dialog for displaying messages
*/
const MessageDialog = ({ open, onClose, title, message }) => {
  return (

    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {title}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Grid container spacing={2}>
          {message}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

/**

Main ModelSelector Component
*/
const ModelSelector = ({ defaultModel = "", setModel = () => { } }) => {
  // ナビゲート
  const navigate = useNavigate()
  const { loginInfo } = useContext(LoginInfo);
  const theme = useTheme();

  // API calls
  const getModelList = async () => {
    try {
      const response = await new ApiFetch().get(
        `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/sd/modelList/1`
      );
      const json = await response.json();
      return json;
    } catch (error) {
      console.error('Error fetching model list:', error);
      return { result: false, data: [] };
    }
  };

  const getSampleImages = async (model, modelId) => {
    try {
      const response = await new ApiFetch().post(
        `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/image/modelSamples`,
        { model, modelId }
      );
      const json = await response.json();
      return json;
    } catch (error) {
      console.error('Error fetching sample images:', error);
      return { result: false, data: [] };
    }
  };

  const toggleFavoriteApi = async (modelId, isFavorite) => {
    try {
      const method = isFavorite ? 'delete' : 'post';
      const response = await new ApiFetch()[method](
        `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/favorite/image_ai_model/${modelId}`,
        method === 'post' ? {} : undefined
      );
      return await response.json();
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return { result: false };
    }
  };

  // State
  const [open, setOpen] = useState(false);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(defaultModel);
  const [dialogSelectedModel, setDialogSelectedModel] = useState(defaultModel);

  // Filter states
  const [showFavorites, setShowFavorites] = useState(false);
  const [selectedModelType, setSelectedModelType] = useState('all');
  const [selectedStyle, setSelectedStyle] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Loading states
  const [favoriteLoading, setFavoriteLoading] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [generateSamplesLoading, setGenerateSamplesLoading] = useState(false);

  // Dialog states
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");

  // 削除ダイアログ用 state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetModel, setDeleteTargetModel] = useState(null);

  // Selected items
  const [selectedModelForGallery, setSelectedModelForGallery] = useState(null);
  const [selectedModelForInfo, setSelectedModelForInfo] = useState(null);
  const [modelImageSamples, setModelImageSamples] = useState([]);

  // Effects
  useEffect(() => {
    setIsLoading(true);
    getModelList().then(response => {
      if (response.result) {
        setModels(response.data);
      }
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (galleryOpen && selectedModelForGallery) {
      setGenerateSamplesLoading(true);
      getSampleImages(selectedModelForGallery.i_name, selectedModelForGallery.id)
        .then(response => {
          if (response.result && response.data.length > 0) {
            setModelImageSamples([...response.data]);
          } else {
            setModelImageSamples([]);
          }
          setGenerateSamplesLoading(false);
        });
    }
  }, [galleryOpen, selectedModelForGallery]);

  // Memoized filtered models
  const filteredModels = useMemo(() => {
    return models.filter(model => {
      const matchesModelType = selectedModelType === 'all' || model.model_type === selectedModelType;
      const matchesStyle = selectedStyle === 'all' || model.style === selectedStyle;
      const matchesSearch = searchQuery === '' ||
        model.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFavorites = !showFavorites || model.favorite === 1;

      return matchesModelType && matchesStyle && matchesSearch && matchesFavorites;
    });
  }, [models, selectedModelType, selectedStyle, searchQuery, showFavorites]);

  // Event handlers
  const handleToggleFavorite = async (modelId) => {
    setFavoriteLoading(prev => ({ ...prev, [modelId]: true }));
    const targetModel = models.find(model => model.id === modelId);
    if (!targetModel) {
      setFavoriteLoading(prev => ({ ...prev, [modelId]: false }));
      return;
    }

    const isFavorite = targetModel.favorite === 1;
    const response = await toggleFavoriteApi(modelId, isFavorite);

    if (response.result) {
      setModels(prevModels => prevModels.map(model => {
        if (model.id === modelId) {
          return { ...model, favorite: model.favorite === 1 ? 0 : 1 };
        }
        return model;
      }));
    } else {
      console.error('Favorite toggle failed:', response);
    }

    setFavoriteLoading(prev => ({ ...prev, [modelId]: false }));
  };

  const handleShowGallery = (modelId) => {
    const model = models.find(m => m.id === modelId);
    if (model) {
      setSelectedModelForGallery(model);
      setGalleryOpen(true);
    }
  };

  const handleShowInfo = (modelId) => {
    const model = models.find(m => m.id === modelId);
    if (model) {
      setSelectedModelForInfo(model);
      setInfoDialogOpen(true);
    }
  };

  const handleShowDialog = (title, message) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setMessageDialogOpen(true);
  };

  // 削除ボタン押下時
  const handleDeleteButton = (model) => {
    setDeleteTargetModel(model);
    setDeleteDialogOpen(true);
  };

  // 削除確定時
  const handleDeleteConfirm = () => {
    // ここでAPI呼び出し等を実装
    setDeleteDialogOpen(false);
    setDeleteTargetModel(null);
    // 必要に応じてメッセージダイアログ等を表示
  };

  // Define breakpoints for grid columns
  const gridBreakpoints = {
    xs: 2, // Extra small devices (phones)
    sm: 3, // Small devices (tablets)
    md: 4, // Medium devices (small laptops)
    lg: 5, // Large devices (desktops)
    xl: 6, // Extra large devices (large desktops)
  };

  return (
    <>
      <Header />
      <Category />
      {/* Message Dialog */}
      <MessageDialog
        open={messageDialogOpen}
        onClose={() => setMessageDialogOpen(false)}
        title={dialogTitle}
        message={dialogMessage}
      />
      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogContent>
          <Typography>本当に「{deleteTargetModel?.name}」を削除しますか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button color="error" onClick={handleDeleteConfirm}>削除</Button>
        </DialogActions>
      </Dialog>
      {/* Model Info Dialog */}
      <ModelInfoDialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        model={selectedModelForInfo}
      />

      {/* Gallery Dialog */}
      <GalleryDialog
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        model={selectedModelForGallery}
        samples={modelImageSamples}
        isLoading={generateSamplesLoading}
        loginInfo={loginInfo}
        onShowDialog={handleShowDialog}
      />

      {/* Main Content - Filter header and model grid */}
      <Box>
        <FilterHeader
          showFavorites={showFavorites}
          setShowFavorites={setShowFavorites}
          selectedStyle={selectedStyle}
          setSelectedStyle={setSelectedStyle}
          selectedModelType={selectedModelType}
          setSelectedModelType={setSelectedModelType}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />

        {/* Model Grid */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredModels.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="body1">該当するモデルが見つかりません</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredModels.map(model => (
                <Grid item
                  key={model.id}
                  xs={12 / gridBreakpoints.xs}
                  sm={12 / gridBreakpoints.sm}
                  md={12 / gridBreakpoints.md}
                  lg={12 / gridBreakpoints.lg}
                  xl={12 / gridBreakpoints.xl}
                >
                  <ModelCard
                    model={model}
                    isSelected={dialogSelectedModel === model.i_name}
                    onSelect={() => {
                      navigate('/model/' + model.model_id);
                      setDialogSelectedModel(model.i_name)
                    }}
                    onToggleFavorite={() => handleToggleFavorite(model.id)}
                    //onShowGallery={() => handleShowGallery(model.id)}
                    onShowInfo={() => handleShowInfo(model.id)}
                    isLoading={favoriteLoading?.[model.id]}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default ModelSelector;